import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { generateUsername, generatePassword, formatMoney } from '@/lib/credit';
import { creditWallet } from '@/lib/wallet';
import { getStoreBalance, transferStoreBalance } from '@/lib/storeBalance';
import { addLoyaltyPoints } from '@/lib/loyalty';

interface Props {
  /** Identifiant Zifek du client (users.id) — null si le client n'est pas rattaché à un compte. */
  clientUserId: number | null;
  clientName: string;
  /** Id du carnet de crédit si connu (fiche du carnet) — évite une résolution. */
  creditCustomerId?: number | null;
  /** Appelé après toute opération réussie (pour rafraîchir les soldes). */
  onDone?: () => void;
}

type ActionKind = 'credit' | 'storeDeposit' | 'walletDeposit' | 'payment' | 'transfer' | 'loyalty';

interface RecipientOption {
  id: number;
  full_name: string;
  linked_user_id: number | null;
}

const ACTION_META: Record<Exclude<ActionKind, 'transfer'>, { title: string; hint: string; submit: string }> = {
  credit: {
    title: 'Ajouter un crédit',
    hint: "Le client prend une somme à crédit chez vous. Sa dette augmente.",
    submit: 'Ajouter le crédit',
  },
  storeDeposit: {
    title: 'Dépôt solde magasin',
    hint: "Le client vous confie des fonds. Ils restent chez votre commerce et sont déduits à chaque achat chez vous.",
    submit: 'Enregistrer le dépôt',
  },
  walletDeposit: {
    title: 'Dépôt portefeuille Zifek',
    hint: "Les fonds sont crédités sur le portefeuille Zifek du client, utilisables partout sur les sites Zifek.",
    submit: 'Créditer le portefeuille',
  },
  payment: {
    title: 'Encaisser un paiement',
    hint: 'Le client rembourse une partie de sa dette. Sa dette diminue.',
    submit: 'Enregistrer le paiement',
  },
  loyalty: {
    title: 'Points de fidélité',
    hint: 'Ajoutez des points (ou retirez-en avec un montant négatif). Le client est notifié.',
    submit: 'Valider',
  },
};

/**
 * ClientZifekActions — boutons d'action marchand sur la fiche d'un client :
 * crédit, dépôt « solde magasin », dépôt « solde portefeuille », encaissement,
 * transfert du solde magasin vers un autre client et points de fidélité.
 */
export default function ClientZifekActions({ clientUserId, clientName, creditCustomerId = null, onDone }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const merchantId = user?.id;
  const currency = user?.monaie || 'MAD';

  const [kind, setKind] = useState<ActionKind | null>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  // Transfert de solde magasin
  const [recipients, setRecipients] = useState<RecipientOption[]>([]);
  const [recipientId, setRecipientId] = useState('');
  const [storeAvailable, setStoreAvailable] = useState<number | null>(null);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  const showFeedback = (ok: boolean, msg: string) => {
    setFeedback({ ok, msg });
    setTimeout(() => setFeedback(null), 4500);
  };

  const closeModal = () => {
    if (busy) return;
    setKind(null);
    setAmount('');
    setNote('');
    setRecipientId('');
    setStoreAvailable(null);
  };

  // Trouve (ou ouvre) la fiche du client dans le carnet de crédit du marchand.
  const resolveCreditCustomer = async (): Promise<number | null> => {
    if (creditCustomerId != null) return creditCustomerId;
    if (!merchantId) return null;
    if (clientUserId != null) {
      const { data } = await supabase
        .from('credit_customers')
        .select('id')
        .eq('merchant_id', merchantId)
        .eq('linked_user_id', clientUserId)
        .maybeSingle();
      if (data?.id) return data.id as number;
    }
    const { data: created, error } = await supabase
      .from('credit_customers')
      .insert({
        merchant_id: merchantId,
        full_name: clientName || 'Client',
        linked_user_id: clientUserId,
        status: 'active',
        username: generateUsername(clientName || 'client'),
        temp_password: generatePassword(),
      })
      .select('id')
      .single();
    if (error) return null;
    return (created?.id as number) ?? null;
  };

  // Charge la liste des autres clients du commerçant + le solde magasin disponible.
  useEffect(() => {
    if (kind !== 'transfer' || !merchantId) return;
    let cancelled = false;
    (async () => {
      setLoadingRecipients(true);
      try {
        const senderId = await resolveCreditCustomer();
        const [listRes, balance] = await Promise.all([
          supabase
            .from('credit_customers')
            .select('id, full_name, linked_user_id')
            .eq('merchant_id', merchantId)
            .order('full_name', { ascending: true }),
          senderId != null ? getStoreBalance(merchantId, senderId) : Promise.resolve({ store: 0, debt: 0 }),
        ]);
        if (cancelled) return;
        setStoreAvailable(balance.store);
        setRecipients(
          ((listRes.data || []) as RecipientOption[]).filter((c) => c.id !== senderId)
        );
      } catch {
        if (!cancelled) {
          setStoreAvailable(0);
          setRecipients([]);
        }
      } finally {
        if (!cancelled) setLoadingRecipients(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, merchantId, creditCustomerId, clientUserId]);

  const submit = async () => {
    if (!merchantId || !kind) return;

    if (kind === 'loyalty') {
      const pts = Math.trunc(Number(String(amount).replace(',', '.')));
      if (!Number.isFinite(pts) || pts === 0) {
        showFeedback(false, 'Saisissez un nombre de points (ex. 20 ou -10).');
        return;
      }
      if (clientUserId == null) {
        showFeedback(false, "Ce client n'est pas rattaché à un compte Zifek.");
        return;
      }
      setBusy(true);
      try {
        const res = await addLoyaltyPoints({
          merchantId,
          clientUserId,
          points: pts,
          description: note || undefined,
          actorId: merchantId,
          actorName: user?.nomcommerce || user?.name || 'commerce',
        });
        if (!res.success) {
          showFeedback(false, res.error || 'Échec de la mise à jour des points.');
          return;
        }
        showFeedback(true, `${pts > 0 ? '+' : ''}${pts} points · nouveau solde : ${res.newTotal ?? 0}`);
        onDone?.();
      } finally {
        setBusy(false);
        setKind(null);
        setAmount('');
        setNote('');
      }
      return;
    }

    const amt = Number(String(amount).replace(',', '.'));
    if (!Number.isFinite(amt) || amt <= 0) {
      showFeedback(false, 'Montant invalide.');
      return;
    }
    setBusy(true);
    try {
      if (kind === 'walletDeposit') {
        if (clientUserId == null) {
          showFeedback(false, "Ce client n'est pas rattaché à un compte Zifek : portefeuille indisponible.");
          return;
        }
        const res = await creditWallet(
          clientUserId,
          amt,
          'commerce',
          note || `Dépôt portefeuille — ${user?.nomcommerce || user?.name || 'commerce'}`
        );
        if (!res.success) {
          showFeedback(false, res.error || 'Échec du dépôt.');
          return;
        }
        showFeedback(true, `${formatMoney(amt, currency)} crédité sur le portefeuille Zifek.`);
        onDone?.();
      } else if (kind === 'transfer') {
        const senderId = await resolveCreditCustomer();
        if (senderId == null) {
          showFeedback(false, "Impossible d'ouvrir le carnet de crédit de ce client.");
          return;
        }
        const target = recipients.find((r) => String(r.id) === recipientId);
        if (!target) {
          showFeedback(false, 'Choisissez le client destinataire.');
          return;
        }
        const res = await transferStoreBalance({
          merchantId,
          fromCustomerId: senderId,
          toCustomerId: target.id,
          amount: amt,
          fromName: clientName || 'Client',
          toName: target.full_name || 'Client',
          actorId: merchantId,
        });
        if (!res.success) {
          showFeedback(false, res.error || 'Échec du transfert.');
          return;
        }
        showFeedback(true, `${formatMoney(amt, currency)} transférés à ${target.full_name}.`);
        onDone?.();
      } else {
        const customerId = await resolveCreditCustomer();
        if (customerId == null) {
          showFeedback(false, "Impossible d'ouvrir le carnet de crédit du client.");
          return;
        }
        const type = kind === 'credit' ? 'credit' : kind === 'storeDeposit' ? 'deposit' : 'payment';
        const { error } = await supabase.from('credit_transactions').insert({
          merchant_id: merchantId,
          customer_id: customerId,
          type,
          amount: amt,
          description: note || null,
          created_by: merchantId,
        });
        if (error) {
          showFeedback(false, "Échec de l'enregistrement.");
          return;
        }
        showFeedback(
          true,
          type === 'credit'
            ? 'Crédit ajouté au carnet.'
            : type === 'deposit'
              ? 'Dépôt solde magasin enregistré.'
              : 'Paiement encaissé.'
        );
        onDone?.();
      }
      setKind(null);
      setAmount('');
      setNote('');
      setRecipientId('');
    } catch {
      showFeedback(false, 'Une erreur est survenue.');
    } finally {
      setBusy(false);
    }
  };

  const isTransfer = kind === 'transfer';
  const meta = kind && !isTransfer ? ACTION_META[kind] : null;

  // Boutons d'action regroupés, organisés en tuiles claires.
  const ACTION_TILES: { kind: ActionKind; label: string; sub: string; icon: string; tone: string }[] = [
    { kind: 'credit', label: 'Ajouter un crédit', sub: 'Le client prend à crédit', icon: 'ri-add-circle-line', tone: 'bg-primary-100 text-primary-700' },
    { kind: 'payment', label: 'Encaisser un paiement', sub: 'Le client rembourse sa dette', icon: 'ri-hand-coin-line', tone: 'bg-primary-100 text-primary-700' },
    { kind: 'storeDeposit', label: 'Dépôt solde magasin', sub: 'Fonds gardés chez vous', icon: 'ri-store-2-line', tone: 'bg-secondary-100 text-secondary-700' },
    { kind: 'walletDeposit', label: 'Dépôt portefeuille', sub: 'Crédité sur le portefeuille Zifek', icon: 'ri-wallet-3-line', tone: 'bg-accent-100 text-accent-700' },
    { kind: 'transfer', label: 'Transférer le solde magasin', sub: 'Vers un autre client du commerce', icon: 'ri-send-plane-line', tone: 'bg-secondary-100 text-secondary-700' },
    { kind: 'loyalty', label: 'Points fidélité', sub: 'Ajouter ou retirer des points', icon: 'ri-star-line', tone: 'bg-accent-100 text-accent-700' },
  ];

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
        {ACTION_TILES.map((tile) => (
          <button
            key={tile.kind}
            onClick={() => setKind(tile.kind)}
            className="group flex items-start gap-3 p-3.5 rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300 hover:bg-primary-50/40 transition-colors text-left cursor-pointer"
          >
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${tile.tone}`}>
              <i className={`${tile.icon} text-base`}></i>
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-foreground-900 leading-snug">{tile.label}</span>
              <span className="block text-[11px] text-foreground-500 leading-snug mt-0.5">{tile.sub}</span>
            </span>
          </button>
        ))}
        <button
          onClick={() => navigate('/dashboard/credit')}
          className="group flex items-start gap-3 p-3.5 rounded-lg border border-background-200/70 bg-background-50 hover:border-foreground-300 hover:bg-background-100 transition-colors text-left cursor-pointer"
        >
          <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-background-100 text-foreground-600">
            <i className="ri-book-2-line text-base"></i>
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-foreground-900 leading-snug">Ouvrir le carnet</span>
            <span className="block text-[11px] text-foreground-500 leading-snug mt-0.5">Voir l&apos;historique complet</span>
          </span>
        </button>
      </div>

      {kind && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={closeModal} />
          <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
            <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 max-h-[92vh] overflow-y-auto">
              <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                  <i className={`${isTransfer ? 'ri-send-plane-line' : 'ri-secure-payment-line'} text-primary-500`}></i>
                  {isTransfer ? 'Transférer le solde magasin' : meta?.title}
                </h3>
                <button
                  onClick={closeModal}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-foreground-500"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-xs text-foreground-500 leading-relaxed flex items-start gap-2">
                  <i className="ri-information-line text-primary-500 mt-0.5"></i>
                  <span>
                    {isTransfer
                      ? "Transférez une partie du solde magasin de ce client vers un autre client de votre commerce. Les fonds restent utilisables uniquement chez vous."
                      : meta?.hint}
                  </span>
                </p>

                {isTransfer && (
                  <div className="flex items-center justify-between text-xs bg-secondary-50 border border-secondary-200/60 rounded-lg px-3 py-2.5">
                    <span className="text-secondary-700">Solde magasin disponible</span>
                    <span className="font-bold text-secondary-800">
                      {loadingRecipients ? '…' : formatMoney(storeAvailable ?? 0, currency)}
                    </span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                    {kind === 'loyalty' ? 'Nombre de points (+ ou -)' : `Montant (${currency})`}
                  </label>
                  <input
                    type="text"
                    inputMode={kind === 'loyalty' ? 'numeric' : 'decimal'}
                    value={amount}
                    onChange={(e) =>
                      setAmount(
                        kind === 'loyalty'
                          ? e.target.value.replace(/[^\d-]/g, '')
                          : e.target.value.replace(/[^\d.,]/g, '')
                      )
                    }
                    placeholder={kind === 'loyalty' ? '20' : '0'}
                    autoFocus
                    className="w-full h-12 px-4 rounded-lg border border-background-200/70 bg-background-50 text-lg font-semibold text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                  />
                </div>

                {isTransfer && (
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Client destinataire</label>
                    {loadingRecipients ? (
                      <div className="h-11 flex items-center text-sm text-foreground-400">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>Chargement...
                      </div>
                    ) : recipients.length === 0 ? (
                      <p className="text-xs text-foreground-500 bg-background-100 rounded-lg px-3 py-2.5">
                        Aucun autre client dans votre carnet. Ajoutez d&apos;abord un client destinataire.
                      </p>
                    ) : (
                      <select
                        value={recipientId}
                        onChange={(e) => setRecipientId(e.target.value)}
                        className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                      >
                        <option value="">Choisir un client…</option>
                        {recipients.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.full_name || `Client #${r.id}`}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                    Note (optionnel)
                  </label>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={120}
                    placeholder="Ex. Achat du 12/10"
                    className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                  />
                </div>
              </div>
              <div className="p-4 border-t border-background-200/70 flex gap-2">
                <button
                  onClick={closeModal}
                  className="flex-1 h-11 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  onClick={submit}
                  disabled={busy || (isTransfer && (loadingRecipients || recipients.length === 0))}
                  className="flex-1 h-11 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                  {isTransfer ? 'Transférer' : meta?.submit}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {feedback && (
        <div
          className={`fixed bottom-20 left-1/2 -translate-x-1/2 z-[60] px-4 py-2.5 rounded-full text-xs font-medium whitespace-nowrap max-w-[92vw] truncate ${
            feedback.ok ? 'bg-primary-600 text-background-50' : 'bg-accent-600 text-background-50'
          }`}
        >
          {feedback.msg}
        </div>
      )}
    </>
  );
}